import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { INLINES } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-html-renderer';

/**
 * Creates custom HTML renderer options for handling inline entries in rich text
 * @returns Options object for documentToHtmlString with custom renderers
 */
export function createHtmlRenderOptions(): Options {
  return {
    renderNode: {
      [INLINES.ENTRY_HYPERLINK]: (node: any, next: any) => {
        const entryTarget = node.data?.target;
        if (!entryTarget) {
          return '';
        }

        // Build the URL using the same logic as the Link component
        const linkInterface = useBuildLinkInterface(entryTarget);
        // next() returns a string in documentToHtmlString, not an array
        const linkText = next(node.content);

        return `<a href="${linkInterface.uri}"${
          entryTarget.fields?.linkTarget === '_blank' ? ' target="_blank"' : ''
        }>${linkText}</a>`;
      },
      [INLINES.ASSET_HYPERLINK]: (node: any, next: any) => {
        const assetTarget = node.data?.target;
        // next() returns a string in documentToHtmlString, not an array
        const linkText = next(node.content);
        const href = assetTarget?.fields?.file?.url || '#';

        return `<a href="${href}" target="_blank" download>${linkText}</a>`;
      },
      [INLINES.EMBEDDED_ENTRY]: (node: any) => {
        const entryTarget = node.data?.target;
        if (!entryTarget) {
          return '';
        }

        // For embedded entries, try to get a meaningful text representation
        if (entryTarget.fields?.linkText) {
          const linkInterface = useBuildLinkInterface(entryTarget);
          return `<a href="${linkInterface.uri}">${entryTarget.fields.linkText}</a>`;
        }

        return (
          entryTarget.fields?.title || entryTarget.fields?.internalName || ''
        );
      }
    }
  };
}

/**
 * Converts contentSlot entries to HTML string with proper link rendering
 * @param contentSlot Array of content entries to convert
 * @param htmlRenderOptions Custom render options for documentToHtmlString
 * @returns HTML string with all entries converted
 */
export function convertContentSlotToHtml(
  contentSlot: any[] | undefined,
  htmlRenderOptions?: Options
): string {
  if (!contentSlot || contentSlot.length === 0) {
    return '';
  }

  const renderOptions = htmlRenderOptions || createHtmlRenderOptions();

  return contentSlot
    .map((entry) => {
      // Handle Paragraph entries with rich text
      if (
        entry.sys?.contentType?.sys?.id === 'paragraph' &&
        entry.fields?.text
      ) {
        return documentToHtmlString(entry.fields.text, renderOptions);
      }

      // Handle other entry types that might have text content
      if (
        entry.sys?.contentType?.sys?.id === 'headline' &&
        entry.fields?.text
      ) {
        return `<h${entry.fields.headingMarkup?.replace('H', '') || '2'}>${
          entry.fields.text
        }</h${entry.fields.headingMarkup?.replace('H', '') || '2'}>`;
      }

      // For other types, try to extract any text content
      if (entry.fields?.text && typeof entry.fields.text === 'object') {
        return documentToHtmlString(entry.fields.text, renderOptions);
      }

      // Fallback for plain text
      if (entry.fields?.text && typeof entry.fields.text === 'string') {
        return entry.fields.text;
      }

      return '';
    })
    .filter(Boolean)
    .join(' ');
}
